import jsonExport from "jsonexport/dist";
import { downloadCSV, useGetIdentity, useListContext, type Exporter } from "ra-core";
import { List } from "@/components/admin/list";
import { ExportButton } from "@/components/admin/export-button";
import { SortButton } from "@/components/admin/sort-button";
import { Card } from "@/components/ui/card";

import { TopToolbar } from "../layout/TopToolbar";
import type { Lead } from "../types";
import { LeadListContent } from "./LeadListContent";
import { LeadListFilter } from "./LeadListFilter";

export const LeadList = () => {
  const { identity } = useGetIdentity();

  if (!identity) return null;

  return (
    <List
      title={false}
      actions={<LeadListActions />}
      perPage={25}
      sort={{ field: "created_at", order: "DESC" }}
      exporter={exporter}
    >
      <LeadListLayout />
    </List>
  );
};

const LeadListLayout = () => {
  const { data, isPending, filterValues } = useListContext();
  const { identity } = useGetIdentity();

  const hasFilters = filterValues && Object.keys(filterValues).length > 0;

  if (!identity || isPending) return null;

  if (!data?.length && !hasFilters) return <LeadEmpty />;

  return (
    <div className="flex flex-row gap-8">
      <LeadListFilter />
      <div className="w-full flex flex-col gap-4">
        <Card className="py-0">
          <LeadListContent />
        </Card>
      </div>
    </div>
  );
};

const LeadListActions = () => (
  <TopToolbar>
    <SortButton fields={["name", "email", "created_at", "concern_level"]} />
    <ExportButton />
  </TopToolbar>
);

const exporter: Exporter<Lead> = async (leads) => {
  const leadsForExport = leads.map((lead) => ({
    id: lead.id,
    name: lead.name,
    email: lead.email,
    title: lead.title || "",
    organization: lead.organization || "",
    comments: lead.comments || "",
    selected_cards: lead.selected_cards?.map((c) => c.name).join("; ") || "",
    ai_characteristics: lead.ai_characteristics?.join("; ") || "",
    ai_characteristics_other: lead.ai_characteristics_other || "",
    ai_providers: lead.ai_providers?.join("; ") || "",
    ai_providers_other: lead.ai_providers_other || "",
    concern_level: lead.concern_level || "",
    who_concerned: lead.who_concerned?.join("; ") || "",
    who_concerned_other: lead.who_concerned_other || "",
    status: lead.status,
    created_at: lead.created_at,
    converted_to_contact_id: lead.converted_to_contact_id || "",
  }));

  jsonExport(leadsForExport, { rowDelimiter: "," }, (err, csv) => {
    if (err) {
      console.error("Export error:", err);
      return;
    }
    downloadCSV(csv, "leads");
  });
};

const LeadEmpty = () => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <h2 className="text-xl font-semibold mb-2">No leads yet</h2>
    <p className="text-muted-foreground max-w-md">
      Leads will appear here when visitors submit the AI Pathologies card request form.
    </p>
  </div>
);
